// frontend/src/components/ActionPlans/ActionPlanTaskModal.jsx
//
// Standalone modal for creating/editing an action_plan_action.
// Extracted from ActionPlanDetail so it can be reused from ActionPlanTasksList.
//
// Props:
//   task              – action row to edit, or null/undefined for new
//   planPublicId      – public_id of the parent action plan (required for save)
//   users             – array of { user_id, full_name }
//   pendingFiles      – File[] for pending attachment upload
//   onPendingFilesChange(files) – called when pending files change
//   onSave(form)      – async, called with the form data; caller handles API + toast
//   onCancel()        – called on cancel / ESC

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import RichTextEditor from '../Common/RichTextEditor';
import SearchableSelect from '../Common/SearchableSelect';
import FileUpload from '../Common/FileUpload';
import { ACTION_STATUS_META, statusPillStyle } from '../../constants/statusMeta';
import { actionPlansAPI, attachmentsAPI } from '../../api/client';
import { FiSave } from 'react-icons/fi';
import '../Settings/Modal.css';

// ── Constants ─────────────────────────────────────────────────────────────────
export const EMPTY_TASK = {
  title: '', description: '', responsible_user_id: null, target_date: '',
  execution_date: '', status: 'NEW', result_notes: '', progress_notes: '',
  completion: 0,
};

const ACTION_STATUSES = ['NEW', 'IN_REVIEW', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];

const COMPLETION_OPTS = Array.from({ length: 21 }, (_, i) => ({
  value: i * 5, label: `${i * 5}%`,
}));

const CompletionCombo = ({ value, onChange }) => (
  <select
    className="form-control ap-completion-select"
    value={value ?? 0}
    onChange={e => onChange(parseInt(e.target.value, 10))}
  >
    {COMPLETION_OPTS.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

// ── Component ─────────────────────────────────────────────────────────────────
const ActionPlanTaskModal = ({
  task,
  planPublicId,
  users = [],
  onSave,
  onCancel,
  onToast,
}) => {
  const { t } = useTranslation();
  const isNew = !task?.public_id;

  const [form, setForm]           = useState({ ...EMPTY_TASK, ...task });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [titleTouched, setTitleTouched]     = useState(!!(task?.title));
  const [pendingDescFiles, setPendingDescFiles]     = useState([]);
  const [pendingResultFiles, setPendingResultFiles] = useState([]);

  // ── Smart setF with auto status/completion rules ──────────────────────────
  const stripHtml = (html) =>
    (html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

  const setF = (k, v) => {
    if (k === 'title') setTitleTouched(!!v);
    setForm(prev => {
      const next = { ...prev, [k]: v };
      // Auto-fill title from description when not manually set
      if (k === 'description' && !titleTouched) {
        const plain = stripHtml(v);
        next.title = plain.slice(0, 120);
      }
      const promotable = ['NEW', 'IN_REVIEW', 'IN_PROGRESS', 'ON_HOLD'];

      if (k === 'completion' || k === 'execution_date') {
        const completion    = k === 'completion'     ? v : next.completion;
        const executionDate = k === 'execution_date' ? v : next.execution_date;

        if ((completion === 100 || executionDate) && promotable.includes(next.status)) {
          next.status = 'COMPLETED';
          if (!next.execution_date)
            next.execution_date = new Date().toISOString().slice(0, 10);
        } else if (completion > 0 && completion < 100 && next.status === 'NEW') {
          next.status = 'IN_PROGRESS';
        }
      }
      return next;
    });
    setHasChanges(true);
  };

  // ── ESC handler ───────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') handleCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasChanges]); // eslint-disable-line

  const handleCancel = () => {
    if (hasChanges && !window.confirm(t('messages.confirm_close', 'Tem alterações não guardadas. Fechar?')))
      return;
    onCancel();
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    const descText = form.description?.replace(/<[^>]*>/g, '').trim() || '';
    if (!descText) { setError('Descrição é obrigatória'); return; }
    setSaving(true);
    try {
      const saved = await onSave(form);
      // Upload pending files — saved.action_id comes from onSave return
      const actionId = saved?.action_id || form.action_id;
      if (actionId) {
        const uploads = [];
        if (pendingDescFiles.length)
          uploads.push(attachmentsAPI.upload('action_plan_action', actionId, pendingDescFiles, '', 'description'));
        if (pendingResultFiles.length)
          uploads.push(attachmentsAPI.upload('action_plan_action', actionId, pendingResultFiles, '', 'result_notes'));
        if (uploads.length) await Promise.all(uploads);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content ap-task-modal">
        <div className="modal-header">
          <h3>{isNew ? 'Nova Tarefa' : 'Editar Tarefa'}</h3>
          <button className="modal-close" type="button" title="Fechar (ESC)" onClick={handleCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              <span className="error-icon">⚠️</span>{error}
            </div>
          )}
          {hasChanges && (
            <div className="form-warning">
              <span className="warning-icon">✏️</span>
              {t('messages.unsaved', 'Tem alterações não guardadas')}
            </div>
          )}

          {/* Status pills */}
          <div className="form-group">
            <label>Estado</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ACTION_STATUSES.map(s => {
                const am = ACTION_STATUS_META[s];
                const active = form.status === s;
                return (
                  <button key={s} type="button"
                    onClick={() => setF('status', s)}
                    style={{
                      padding: '4px 11px', borderRadius: 20, cursor: 'pointer',
                      fontSize: 12, border: '1.5px solid', transition: 'all 0.1s',
                      ...statusPillStyle(am, active),
                    }}>
                    {am.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div className="form-group">
            <label>Título</label>
            <input
              type="text"
              className="form-control"
              value={form.title || ''}
              onChange={e => setF('title', e.target.value)}
              placeholder=""
              maxLength={120}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Descrição *</label>
            <RichTextEditor
              value={form.description || ''}
              onChange={html => setF('description', html)}
              placeholder="Descreva a tarefa a executar…"
              minHeight={110}
              pendingFiles={pendingDescFiles}
              onPendingFilesChange={setPendingDescFiles}
              onToast={onToast}
              attachEntityType={form.action_id ? 'action_plan_action' : null}
              attachEntityId={form.action_id || null}
              attachCategory="description"
            />
          </div>

          {/* Responsável + Prazo + Data Conclusão + Conclusão % */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '8px 10px', alignItems: 'end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Responsável</label>
              <SearchableSelect
                value={form.responsible_user_id}
                onChange={v => setF('responsible_user_id', v ? Number(v) : null)}
                options={users}
                getOptionLabel={u => u.full_name}
                getOptionValue={u => u.user_id}
                placeholder="— selecionar —"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Prazo</label>
              <input type="date" className="form-control" style={{ width: 148 }}
                value={form.target_date?.slice(0, 10) || ''}
                onChange={e => setF('target_date', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Data Conclusão</label>
              <input type="date" className="form-control" style={{ width: 148 }}
                value={form.execution_date?.slice(0, 10) || ''}
                onChange={e => setF('execution_date', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Conclusão %</label>
              <CompletionCombo value={form.completion ?? 0} onChange={v => setF('completion', v)} />
            </div>
          </div>

          {/* Result notes */}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Resultado / Notas</label>
            <RichTextEditor
              value={form.result_notes || ''}
              onChange={html => setF('result_notes', html)}
              placeholder="Resultado obtido, evidências…"
              minHeight={90}
              pendingFiles={pendingResultFiles}
              onPendingFilesChange={setPendingResultFiles}
              onToast={onToast}
              attachEntityType={form.action_id ? 'action_plan_action' : null}
              attachEntityId={form.action_id || null}
              attachCategory="result_notes"
            />
          </div>



          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              <FiSave size={14} /> {saving ? 'A guardar…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActionPlanTaskModal;